// 「與我共用的攝影機」 (spec 18 §4–§5) — GET /v3/me/shared-with-me?status=active,pending.
// Pending invites render at the top with accept/decline; active rows offer self-exit.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { revokedTombstones } from '../devices/revokedTombstones';

const PERMISSION_LABEL_KEY: Record<string, string> = {
  view: 'account.permView',
  subscribe: 'account.permSubscribe',
  control: 'account.permControl',
  'recording:read': 'account.permRecordingRead',
};

interface SharedItem {
  device_id: string;
  display_name: string;
  model?: string;
  owner_display_name?: string | null;
  relation_status: 'active' | 'pending';
  permissions?: string[];
  invite_id?: string;
  online?: { state?: string };
}

export default function SharedWithMeCard() {
  const { t } = useTranslation();
  const [items, setItems] = useState<SharedItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch<SharedItem[]>('/v3/me/shared-with-me', {
        query: { status: 'active,pending', limit: 50 },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = useMemo(() => (items ?? []).filter((i) => i.relation_status === 'pending'), [items]);
  const active = useMemo(() => (items ?? []).filter((i) => i.relation_status === 'active'), [items]);

  const respond = useCallback(
    async (item: SharedItem, action: 'accept' | 'decline') => {
      if (!item.invite_id) return;
      setBusyKey(item.invite_id);
      setActionError(null);
      setNotice(null);
      try {
        await apiFetch(`/v3/shares/${item.invite_id}/${action}`, {
          method: 'POST',
          idempotencyKey: crypto.randomUUID(),
        });
        // 撤銷後 30s 內重新受邀同一台:清 tombstone,否則清單 reload 會把新關係濾掉(APP#123)。
        if (action === 'accept') revokedTombstones.clear(item.device_id);
        setNotice(action === 'accept' ? t('account.inviteAccepted', { name: item.display_name }) : t('account.inviteDeclined'));
      } catch (e) {
        if (e instanceof ApiError) {
          // 18 §4.1 error mapping
          const map: Record<string, string> = {
            INVITE_NOT_FOUND: t('account.errInviteNotFound'),
            INVITE_NOT_PENDING: t('account.errInviteNotPending'),
            INVITE_EXPIRED: t('account.errInviteExpired'),
            FORBIDDEN: t('account.errInviteForbidden'),
          };
          setActionError(map[e.code] ?? `${e.message}（${e.code}）`);
        } else {
          setActionError(t('common.actionFailedNetwork'));
        }
      } finally {
        setBusyKey(null);
        await load(); // realign state with server in every case
      }
    },
    [load, t]
  );

  const leave = useCallback(
    async (item: SharedItem) => {
      if (!window.confirm(t('account.leaveShareConfirm', { name: item.display_name }))) return;
      setBusyKey(item.device_id);
      setActionError(null);
      setNotice(null);
      try {
        await apiFetch(`/v3/devices/${item.device_id}/shares/me`, {
          method: 'DELETE',
          idempotencyKey: crypto.randomUUID(),
        });
      } catch (e) {
        // 404 SHARE_NOT_FOUND → already out: idempotent success (18 §5.2)
        if (!(e instanceof ApiError && e.code === 'SHARE_NOT_FOUND')) {
          setActionError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.actionFailedRetry'));
        }
      } finally {
        setBusyKey(null);
        await load();
      }
    },
    [load, t]
  );

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>
        {t('account.sharedWithMe')}
        {pending.length > 0 && (
          <span
            style={{
              marginLeft: 'var(--spacing-4)',
              color: 'var(--color-semantic-warning)',
              fontSize: 'var(--font-size-md)',
            }}
          >
            {t('account.pendingInviteCount', { count: pending.length })}
          </span>
        )}
      </h2>

      {loading && <p className="text-secondary">{t('common.loading')}</p>}
      {!loading && error && (
        <div>
          <p className="error-text">{error}</p>
          <button className="btn-ghost" onClick={() => void load()}>
            {t('common.retry')}
          </button>
        </div>
      )}
      {actionError && <p className="error-text">{actionError}</p>}
      {notice && <p style={{ color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{notice}</p>}

      {!loading && !error && items && items.length === 0 && (
        <p className="text-secondary">{t('account.sharedEmpty')}</p>
      )}

      {pending.length > 0 && (
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <h3 style={{ fontSize: 'var(--font-size-lg)', margin: '0 0 var(--spacing-3)' }}>{t('account.pendingInvites')}</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {pending.map((item) => (
              <li
                key={item.invite_id ?? item.device_id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--spacing-4)',
                  padding: 'var(--spacing-4) 0',
                  borderTop: '1px solid var(--color-surface-line)',
                }}
              >
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 'var(--font-weight-medium)' }}>{item.display_name}</div>
                  <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                    {t('account.sharedByGranted', {
                      owner: item.owner_display_name ?? '—',
                      permissions: item.permissions?.length
                        ? item.permissions.map((p) => (PERMISSION_LABEL_KEY[p] ? t(PERMISSION_LABEL_KEY[p]) : p)).join('、')
                        : t('account.defaultPermissions'),
                    })}
                  </div>
                </div>
                <button
                  className="btn-primary"
                  disabled={busyKey !== null}
                  onClick={() => void respond(item, 'accept')}
                >
                  {busyKey === item.invite_id ? t('common.processing') : t('account.accept')}
                </button>
                <button className="btn-ghost" disabled={busyKey !== null} onClick={() => void respond(item, 'decline')}>
                  {t('account.decline')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {active.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {active.map((item) => (
            <li
              key={item.device_id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--spacing-4)',
                padding: 'var(--spacing-4) 0',
                borderTop: '1px solid var(--color-surface-line)',
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 'var(--font-weight-medium)' }}>
                  <Link to={`/devices/${item.device_id}`}>{item.display_name}</Link>{' '}
                  {item.online?.state === 'online' ? (
                    <span style={{ color: 'var(--color-live-live)', fontSize: 'var(--font-size-sm)' }}>{t('account.online')}</span>
                  ) : (
                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('account.offline')}</span>
                  )}
                </div>
                <div className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                  {t('account.sharedByPermissions', {
                    owner: item.owner_display_name ?? '—',
                    permissions: item.permissions?.length
                      ? item.permissions.map((p) => (PERMISSION_LABEL_KEY[p] ? t(PERMISSION_LABEL_KEY[p]) : p)).join('、')
                      : t('account.defaultPermissions'),
                  })}
                </div>
              </div>
              <button className="btn-danger" disabled={busyKey !== null} onClick={() => void leave(item)}>
                {busyKey === item.device_id ? t('common.processing') : t('account.leaveShare')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
